/**
 * CineEngine — Nonogram (Picross) generator + solver
 * Algorithm ported from Simon Tatham's pattern.c (MIT licence)
 * Guarantees: unique solution, line-by-line deducible, seed-reproducible
 */

export interface CinePuzzle {
  w: number;
  h: number;
  rowClues: number[][];
  colClues: number[][];
  solution: Uint8Array; // 0 = empty, 1 = filled
  seed: number;
  difficulty: number;
}

export const LEVEL_CONFIG: Record<number, { w: number; h: number; name: string; icon: string }> = {
  1: { w: 5,  h: 5,  name: 'Neurona',  icon: '🧠' },
  2: { w: 8,  h: 8,  name: 'Sinapsis', icon: '⚡' },
  3: { w: 10, h: 10, name: 'Cerebro',  icon: '🧩' },
  4: { w: 12, h: 12, name: 'Genio',    icon: '💡' },
  5: { w: 15, h: 15, name: 'Einstein', icon: '🎓' },
};

// ─── PRNG ────────────────────────────────────────────────────────────────────

class PRNG {
  private m = 0x80000000;
  private a = 1103515245;
  private c = 12345;
  private state: number;

  constructor(seed: number) { this.state = seed; }

  nextInt(): number {
    this.state = (this.a * this.state + this.c) % this.m;
    return this.state;
  }

  nextFloat(): number { return this.nextInt() / (this.m - 1); }
  nextRange(max: number): number { return Math.floor(this.nextFloat() * max); }
}

// ─── Solver constants ────────────────────────────────────────────────────────

const UNKNOWN       = 0;
const BLOCK         = 1;
const DOT           = 2;
const STILL_UNKNOWN = 3;

// ─── Clue extraction ─────────────────────────────────────────────────────────

function computeRowdata(grid: Uint8Array, start: number, len: number, step: number): number[] {
  const result: number[] = [];
  for (let i = 0; i < len; i++) {
    if (grid[start + i * step] === 1) {
      let run = 1;
      while (i + run < len && grid[start + (i + run) * step] === 1) run++;
      result.push(run);
      i += run - 1;
    }
  }
  return result;
}

// ─── Line solver (ported from do_recurse / do_row in pattern.c) ──────────────

function doRecurse(
  known:      Uint8Array,
  deduced:    Uint8Array,
  row:        Uint8Array,
  minDone:    Uint8Array,
  maxDone:    Uint8Array,
  minOk:      Uint8Array,
  maxOk:      Uint8Array,
  data:       number[],
  len:        number,
  freespace:  number,
  ndone:      number,
  lowest:     number,
): boolean {
  if (data[ndone] !== 0) {
    // Memoization: if we've already explored block ndone starting at lowest, reuse
    if (lowest >= minDone[ndone] && lowest <= maxDone[ndone]) {
      if (lowest >= minOk[ndone] && lowest <= maxOk[ndone]) {
        for (let i = 0; i < lowest; i++) deduced[i] |= row[i];
      }
      return lowest >= minOk[ndone] && lowest <= maxOk[ndone];
    }
    if (lowest < minDone[ndone]) minDone[ndone] = lowest;
    if (lowest > maxDone[ndone]) maxDone[ndone] = lowest;

    for (let i = 0; i <= freespace; i++) {
      let j = lowest;
      let valid = true;

      for (let k = 0; k < i; k++) {
        if (known[j] === BLOCK) { valid = false; break; }
        row[j++] = DOT;
      }
      if (!valid) continue;

      for (let k = 0; k < data[ndone]; k++) {
        if (known[j] === DOT) { valid = false; break; }
        row[j++] = BLOCK;
      }
      if (!valid) continue;

      if (j < len) {
        if (known[j] === BLOCK) continue;
        row[j++] = DOT;
      }

      if (doRecurse(known, deduced, row, minDone, maxDone, minOk, maxOk, data, len, freespace - i, ndone + 1, j)) {
        if (lowest < minOk[ndone])         minOk[ndone]  = lowest;
        if (lowest + i > maxOk[ndone])     maxOk[ndone]  = lowest + i;
        if (lowest + i > maxDone[ndone])   maxDone[ndone] = lowest + i;
      }
    }
    return lowest >= minOk[ndone] && lowest <= maxOk[ndone];
  } else {
    // No more blocks — fill remainder with DOT
    for (let i = lowest; i < len; i++) {
      if (known[i] === BLOCK) return false;
      row[i] = DOT;
    }
    for (let i = 0; i < len; i++) deduced[i] |= row[i];
    return true;
  }
}

function doRow(
  known:   Uint8Array,
  deduced: Uint8Array,
  row:     Uint8Array,
  minDone: Uint8Array,
  maxDone: Uint8Array,
  minOk:   Uint8Array,
  maxOk:   Uint8Array,
  matrix:  Uint8Array,
  offset:  number,
  len:     number,
  step:    number,
  data:    number[],
  changed: Uint32Array | null,
): boolean {
  let rowlen = 0;
  let freespace = len + 1;

  for (rowlen = 0; data[rowlen] !== 0; rowlen++) {
    minDone[rowlen] = len - 1;
    maxDone[rowlen] = 0;
    minOk[rowlen]   = len - 1;
    maxOk[rowlen]   = 0;
    freespace -= data[rowlen] + 1;
  }

  for (let i = 0; i < len; i++) {
    known[i]   = matrix[offset + i * step];
    deduced[i] = 0;
  }

  for (let i = len - 1; i >= 0 && known[i] === DOT; i--) freespace--;

  if (rowlen === 0) {
    for (let i = 0; i < len; i++) deduced[i] = DOT;
  } else if (rowlen === 1 && data[0] === len) {
    for (let i = 0; i < len; i++) deduced[i] = BLOCK;
  } else {
    doRecurse(known, deduced, row, minDone, maxDone, minOk, maxOk, data, len, freespace, 0, 0);
  }

  let doneAny = false;
  for (let i = 0; i < len; i++) {
    if (deduced[i] !== 0 && deduced[i] !== STILL_UNKNOWN && matrix[offset + i * step] === UNKNOWN) {
      matrix[offset + i * step] = deduced[i];
      if (changed) changed[i]++;
      doneAny = true;
    }
  }
  return doneAny;
}

// ─── Puzzle solver ────────────────────────────────────────────────────────────

function solvePuzzle(w: number, h: number, rowClues: number[][], colClues: number[][]): boolean {
  const maxDim = Math.max(w, h);
  const matrix  = new Uint8Array(w * h);
  const known   = new Uint8Array(maxDim);
  const deduced = new Uint8Array(maxDim);
  const row     = new Uint8Array(maxDim);
  const minDone = new Uint8Array(maxDim);
  const maxDone = new Uint8Array(maxDim);
  const minOk   = new Uint8Array(maxDim);
  const maxOk   = new Uint8Array(maxDim);
  const changedH = new Uint32Array(h + 1);
  const changedW = new Uint32Array(w + 1);

  // Initialise priorities based on overlap potential
  for (let i = 0; i < h; i++) {
    const clue = rowClues[i];
    if (clue.length === 0) { changedH[i] = w; continue; }
    let free = w + 1;
    for (const d of clue) free -= d + 1;
    changedH[i] = 0;
    for (const d of clue) if (d > free) changedH[i] += d - free;
  }
  let maxH = 0;
  for (let i = 0; i < h; i++) if (changedH[i] > maxH) maxH = changedH[i];

  for (let j = 0; j < w; j++) {
    const clue = colClues[j];
    if (clue.length === 0) { changedW[j] = h; continue; }
    let free = h + 1;
    for (const d of clue) free -= d + 1;
    changedW[j] = 0;
    for (const d of clue) if (d > free) changedW[j] += d - free;
  }
  let maxW = 0;
  for (let j = 0; j < w; j++) if (changedW[j] > maxW) maxW = changedW[j];

  do {
    for (; maxH > 0 && maxH >= maxW; maxH--) {
      for (let i = 0; i < h; i++) {
        if (changedH[i] >= maxH) {
          doRow(known, deduced, row, minDone, maxDone, minOk, maxOk,
                matrix, i * w, w, 1, [...rowClues[i], 0], changedW);
          changedH[i] = 0;
        }
      }
      maxW = 0;
      for (let j = 0; j < w; j++) if (changedW[j] > maxW) maxW = changedW[j];
    }
    for (; maxW > 0 && maxW >= maxH; maxW--) {
      for (let j = 0; j < w; j++) {
        if (changedW[j] >= maxW) {
          doRow(known, deduced, row, minDone, maxDone, minOk, maxOk,
                matrix, j, h, w, [...colClues[j], 0], changedH);
          changedW[j] = 0;
        }
      }
      maxH = 0;
      for (let i = 0; i < h; i++) if (changedH[i] > maxH) maxH = changedH[i];
    }
  } while (maxH > 0 || maxW > 0);

  for (let i = 0; i < w * h; i++) if (matrix[i] === UNKNOWN) return false;
  return true;
}

// ─── Grid generator (cellular automaton + threshold, from pattern.c) ─────────

function generateGrid(rng: PRNG, w: number, h: number): Uint8Array {
  const fg: number[] = Array.from({ length: w * h }, () => rng.nextFloat());

  const sm: number[] = new Array(w * h);
  for (let i = 0; i < h; i++) {
    for (let j = 0; j < w; j++) {
      let n = 0, sx = 0;
      for (let p = -1; p <= 1; p++) {
        for (let q = -1; q <= 1; q++) {
          if (i + p < 0 || i + p >= h || j + q < 0 || j + q >= w) continue;
          if (h === 2 && p !== 0) continue;
          if (w === 2 && q !== 0) continue;
          n++; sx += fg[(i + p) * w + (j + q)];
        }
      }
      sm[i * w + j] = sx / n;
    }
  }

  const sorted = [...sm].sort((a, b) => a - b);
  let idx = Math.floor(w * h / 2);
  if ((w & 1) && (h & 1)) idx += rng.nextRange(2);
  if (idx >= w * h) idx = w * h - 1;
  const threshold = sorted[idx];

  const grid = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) grid[i] = sm[i] >= threshold ? 1 : 0;
  return grid;
}

function generateSoluble(rng: PRNG, w: number, h: number) {
  for (;;) {
    const grid = generateGrid(rng, w, h);

    let ok = true;
    if (w > 2) {
      for (let i = 0; i < h && ok; i++) {
        let colors = 0;
        for (let j = 0; j < w; j++) colors |= grid[i * w + j] ? 2 : 1;
        if (colors !== 3) ok = false;
      }
    }
    if (ok && h > 2) {
      for (let j = 0; j < w && ok; j++) {
        let colors = 0;
        for (let i = 0; i < h; i++) colors |= grid[i * w + j] ? 2 : 1;
        if (colors !== 3) ok = false;
      }
    }
    if (!ok) continue;

    const rowClues: number[][] = Array.from({ length: h }, (_, i) => computeRowdata(grid, i * w, w, 1));
    const colClues: number[][] = Array.from({ length: w }, (_, j) => computeRowdata(grid, j, h, w));

    if (solvePuzzle(w, h, rowClues, colClues)) return { grid, rowClues, colClues };
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function createGame(difficulty: number, seedOverride?: number): CinePuzzle {
  const cfg  = LEVEL_CONFIG[difficulty] ?? LEVEL_CONFIG[1];
  const seed = seedOverride ?? (Date.now() & 0x7fffffff);
  const rng  = new PRNG(seed);
  const { grid, rowClues, colClues } = generateSoluble(rng, cfg.w, cfg.h);
  return { w: cfg.w, h: cfg.h, rowClues, colClues, solution: grid, seed, difficulty };
}
